import React, { useEffect, useState } from 'react';
import { App, Button, Tooltip } from 'antd';
import { NotificationOutlined, NotificationFilled } from '@ant-design/icons';
import { useAppSelector } from '../../hooks/redux';
import { isChef } from '../../utils/roleHelpers';
import {
  useGetPushPublicKeyQuery,
  useSubscribePushMutation,
  useUnsubscribePushMutation,
} from '../../services/notificationsApi';
import {
  isPushSupported,
  subscribeToPush,
  unsubscribeFromPush,
  getCurrentPushSubscription,
  PUSH_ENABLED_KEY,
} from '../../utils/pushNotifications';

/**
 * Botón del header para activar/desactivar las notificaciones push de
 * "orden lista" en ESTE dispositivo (pantalla bloqueada / app en background).
 * La alerta in-app por SignalR funciona siempre, con o sin esto.
 */
const PushNotificationsToggle: React.FC = () => {
  const { message } = App.useApp();
  const { user } = useAppSelector((state) => state.auth);

  const supported = isPushSupported();
  const relevantRole = !!user && !isChef(user.role);

  const { data: keyData } = useGetPushPublicKeyQuery(undefined, {
    skip: !relevantRole || !supported,
  });
  const [subscribePush] = useSubscribePushMutation();
  const [unsubscribePush] = useUnsubscribePushMutation();

  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);

  const publicKey = keyData?.publicKey || null;

  // Al montar: refleja el estado real y re-suscribe en silencio si el usuario
  // ya había activado push en este dispositivo (re-asigna la suscripción al
  // usuario actual — importante en tablets compartidas entre meseros)
  useEffect(() => {
    if (!supported || !publicKey || !relevantRole) return;
    let cancelled = false;

    (async () => {
      const existing = await getCurrentPushSubscription();
      const optedIn = localStorage.getItem(PUSH_ENABLED_KEY) === '1';
      if (cancelled) return;

      if (existing && Notification.permission === 'granted' && optedIn) {
        setEnabled(true);
        try {
          const dto = await subscribeToPush(publicKey);
          if (dto && !cancelled) {
            await subscribePush(dto).unwrap();
          }
        } catch {
          // sin red o sesión expirada: se reintenta en el próximo login
        }
      } else {
        setEnabled(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [supported, publicKey, relevantRole, user?.id, subscribePush]);

  if (!supported || !publicKey || !relevantRole) {
    return null;
  }

  const handleToggle = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (!enabled) {
        const dto = await subscribeToPush(publicKey);
        if (!dto) {
          message.warning('Permiso de notificaciones denegado. Actívalo en la configuración del navegador.');
          return;
        }
        await subscribePush(dto).unwrap();
        localStorage.setItem(PUSH_ENABLED_KEY, '1');
        setEnabled(true);
        message.success('Este dispositivo recibirá alertas de órdenes listas, aun con la pantalla bloqueada');
      } else {
        const endpoint = await unsubscribeFromPush();
        if (endpoint) {
          await unsubscribePush({ endpoint }).unwrap();
        }
        localStorage.removeItem(PUSH_ENABLED_KEY);
        setEnabled(false);
        message.info('Alertas desactivadas en este dispositivo');
      }
    } catch (err) {
      console.error('Error al cambiar la suscripción push:', err);
      message.error('No se pudo actualizar la suscripción de notificaciones');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Tooltip title={enabled ? 'Alertas de órdenes listas: activadas en este dispositivo' : 'Activar alertas de órdenes listas en este dispositivo'}>
      <Button
        type="text"
        size="middle"
        loading={busy}
        onClick={handleToggle}
        icon={
          enabled
            ? <NotificationFilled style={{ color: 'var(--color-primary)' }} />
            : <NotificationOutlined />
        }
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      />
    </Tooltip>
  );
};

export default PushNotificationsToggle;
